import {
  findAgentCcpStatus,
  normalizeResourceId,
} from '@/utils/managerTeamMetrics';
import { AgentStatusDisplayData } from '@/components/common/AgentStatusTable';
import type { GetAgentCcpStatusForUsersOutputDTO } from '@/types/agents';
import type {
  GetAgentQueueStatusOutputDTO,
  AgentQueueStatusDTO,
} from '@/types/agentEvents';
import type { GetRoutingProfilesOutputDTO } from '@/types/routingProfile';
import type { TFunction } from 'i18next';

interface AgentMember {
  connectId: string;
  userEmail: string;
  routingProfileId?: string;
  routingProfileName?: string;
}

/**
 * Transforms raw agent data into displayable status data
 */
export function transformMembersToStatusData(
  members: AgentMember[] | undefined,
  agentCcpStatusData: GetAgentCcpStatusForUsersOutputDTO | undefined,
  allAgentsQueueData: GetAgentQueueStatusOutputDTO | undefined,
  routingProfilesData: GetRoutingProfilesOutputDTO | undefined,
  routingProfileName?: string,
  t?: TFunction
): AgentStatusDisplayData[] {
  if (!members) return [];

  const routingProfileNameById = new Map<string, string>();
  routingProfilesData?.routingProfiles?.forEach(profile => {
    routingProfileNameById.set(normalizeResourceId(profile.id), profile.name);
  });

  const queueStatusByAgent = new Map<string, AgentQueueStatusDTO[]>();
  if (allAgentsQueueData?.data) {
    allAgentsQueueData.data.forEach((queueEntry: AgentQueueStatusDTO) => {
      if (queueEntry.agentId) {
        if (!queueStatusByAgent.has(queueEntry.agentId)) {
          queueStatusByAgent.set(queueEntry.agentId, []);
        }
        queueStatusByAgent.get(queueEntry.agentId)!.push(queueEntry);
      }
    });
  }

  return members
    .map(member => {
      const agentCcpStatus = findAgentCcpStatus(
        agentCcpStatusData?.users,
        member.connectId
      );
      const status = agentCcpStatus?.status;

      const routingProfileKey = member.routingProfileId
        ? normalizeResourceId(member.routingProfileId)
        : '';

      const agentQueues = queueStatusByAgent.get(member.connectId || '');
      const queueInfo =
        agentQueues && agentQueues.length > 0
          ? {
              queueName: agentQueues.map(queue => queue.queueName).join(', '),
              waitingContacts: agentQueues.reduce(
                (sum, queue) => sum + queue.waitingCount,
                0
              ),
            }
          : null;

      const finalRoutingProfileName =
        (routingProfileKey && routingProfileNameById.get(routingProfileKey)) ||
        member.routingProfileName ||
        routingProfileName ||
        (t && t('components.manager.liveAgentStatus.N/A')) ||
        'N/A';

      return {
        email: member.userEmail,
        connectId: member.connectId || '',
        status: status || 'Unknown',
        statusStartTime: agentCcpStatus?.statusStartTimestamp || null,
        routingProfileName: finalRoutingProfileName,
        activeCalls: agentCcpStatus?.callsCount || 0,
        queueInfo,
      } as AgentStatusDisplayData;
    })
    .sort((a, b) => a.email.localeCompare(b.email));
}
